import React from 'react'

import { EmtAutocomplete, EmtAutocompleteOption, EmtIcon, EmtTypography } from 'emt-components-v2'
import { UserServiceHelper } from '@/modules/User'
import { useTranslation } from 'next-i18next'
import Box from '@mui/material/Box'
import { Address, AddressProvider } from '@/dataV3/Common/Address'

type Props = {
  label: string
  address: Address | null
  onAddressChange: (address: Address | null) => void | Promise<void>
  addressError?: string
  onAddressError?: (errorMessage: string) => void | Promise<void>
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  iconLeftClassName?: string
  strict?: boolean
}

type AddressOption = EmtAutocompleteOption

const AddressAutocomplete: React.FC<Props> = ({
  label,
  onFocus,
  onBlur,
  address,
  onAddressChange,
  addressError,
  onAddressError,
  iconLeftClassName,
  strict,
}) => {
  const { t } = useTranslation('common')

  const refreshAddressTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const cacheRef = React.useRef<Map<string, AddressProvider[]>>(new Map())
  const inputRef = React.useRef<HTMLInputElement | null>(null)

  const [isAddressLoading, setAddressLoading] = React.useState(false)
  const [addressSuggestionOptions, setAddressSuggestionOptions] = React.useState<AddressOption[]>([])
  const [addressInput, setAddressInput] = React.useState('')
  const [addressOption, setAddressOption] = React.useState<AddressOption | null>(null)
  const [isGeolocationAvailable, setGeolocationAvailable] = React.useState(false)
  const [isOpen, setOpen] = React.useState(false)

  const MIN_CHARS = strict ? 15 : 3
  const normalizedInputLength = addressInput.trim().length

  const closeDropdownAndKeyboard = () => {
    setOpen(false)
    inputRef.current?.blur()
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }

  React.useEffect(() => {
    if (!('geolocation' in navigator)) {
      setGeolocationAvailable(false)
      return
    }

    setGeolocationAvailable(true)

    let permissionStatus: PermissionStatus | undefined
    let updateAvailability: (() => void) | undefined
    let isDisposed = false

    navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((status) => {
        if (isDisposed) return

        permissionStatus = status
        updateAvailability = () => setGeolocationAvailable(status.state !== 'denied')
        updateAvailability()
        status.addEventListener('change', updateAvailability)
      })
      .catch(() => undefined)

    return () => {
      isDisposed = true
      if (updateAvailability) permissionStatus?.removeEventListener('change', updateAvailability)
    }
  }, [])

  React.useEffect(() => {
    if (address) {
      setAddressOption({ id: address['@id'], label: address.formatted })
    } else {
      setAddressOption(null)
    }
  }, [address])

  const normalizeKey = (value: string) => value.trim().toLowerCase()

  const updateSuggestionsAsync = async (text: string) => {
    const key = normalizeKey(text)

    // CACHE
    const cached = cacheRef.current.get(key)
    if (cached) {
      setAddressSuggestionOptions(
        cached.map((p) => ({
          id: p.providerId,
          label: p.formated, // backend typo assumé
        })),
      )
      setAddressLoading(false)
      onAddressError?.('')
      return
    }

    try {
      const providers = await UserServiceHelper.searchAddressAsync2({ input: text, strict })

      cacheRef.current.set(key, providers)

      setAddressSuggestionOptions(
        providers.map((p) => ({
          id: p.providerId,
          label: p.formated,
        })),
      )

      onAddressError?.('')
    } catch (err) {
      console.warn('searchAddressAsync', err)
      onAddressError?.(t('addressAutocomplete.errors.generic'))
      setOpen(false)
    } finally {
      setAddressLoading(false)
    }
  }

  const onAddressInputChange = (_: React.SyntheticEvent, value: string) => {
    setAddressInput(value)
  }

  const lastQueryRef = React.useRef<string>('')

  React.useEffect(() => {
    if (refreshAddressTimeoutRef.current) {
      clearTimeout(refreshAddressTimeoutRef.current)
    }

    const normalizedInput = normalizeKey(addressInput)

    // ❌ ignore si uniquement des espaces ou même valeur normalisée
    if (!normalizedInput || normalizedInput.length < MIN_CHARS) {
      setAddressSuggestionOptions([])
      setAddressLoading(false)
      return
    }

    // ❌ évite requête si le trim n’a rien changé (ex: ajout d'espaces)
    if (normalizedInput === lastQueryRef.current) {
      setAddressLoading(false)
      return
    }

    setAddressLoading(true)

    refreshAddressTimeoutRef.current = setTimeout(() => {
      lastQueryRef.current = normalizedInput
      updateSuggestionsAsync(addressInput)
    }, 900)

    return () => {
      if (refreshAddressTimeoutRef.current) {
        clearTimeout(refreshAddressTimeoutRef.current)
      }
    }
  }, [addressInput, addressOption])

  const onChange = async (_: React.SyntheticEvent, newValue: EmtAutocompleteOption | null) => {
    if (!newValue) {
      onAddressChange(null)
      setOpen(false)
      return
    }

    try {
      const address = await UserServiceHelper.retrieveAddressAsync(newValue.id)
      onAddressChange(address)
    } catch (err) {
      console.warn('retrieveAddressAsync', err)
      onAddressError?.(t('addressAutocomplete.errors.generic'))
    }

    setOpen(false)
  }

  const getLocationAsync = (): Promise<GeolocationPosition> =>
    new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject)
    })

  const onLocationClick = async () => {
    try {
      setAddressLoading(true)

      const position = await getLocationAsync()

      const address = await UserServiceHelper.searchAddressAsync2({
        geolocation: {
          latitude: position.coords.latitude.toString(),
          longitude: position.coords.longitude.toString(),
        },
        strict,
      })

      const first = address[0]
      if (!first) throw new Error('Error on geolocation')

      onAddressChange(first)

      requestAnimationFrame(() => {
        closeDropdownAndKeyboard()
      })
    } catch (err) {
      console.warn('onLocationClick', err)

      if (typeof err === 'object' && err !== null && 'code' in err && err.code === 1) {
        setGeolocationAvailable(false)
        onAddressError?.(t('addressAutocomplete.errors.permissionDenied'))
        return
      }

      onAddressError?.(t('addressAutocomplete.errors.generic'))
    } finally {
      setAddressLoading(false)
    }
  }

  return (
    <EmtAutocomplete
      options={addressSuggestionOptions}
      open={isOpen}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      filterOptions={(x) => x}
      fullWidth
      label={label}
      iconType={isGeolocationAvailable ? 'location' : 'search'}
      iconLeftClassName={iconLeftClassName}
      onIconClick={isGeolocationAvailable ? onLocationClick : undefined}
      value={addressOption}
      onInputChange={onAddressInputChange}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      helperText={addressError}
      error={!!addressError?.length}
      loader={isAddressLoading}
      noOptionsText={
        normalizedInputLength > 0 && normalizedInputLength < MIN_CHARS ? (
          t('addressAutocomplete.minChars', { count: MIN_CHARS })
        ) : normalizedInputLength >= MIN_CHARS && !addressSuggestionOptions.length && !addressOption ? (
          t('addressAutocomplete.noOptionsText')
        ) : !isGeolocationAvailable ? (
          t('addressAutocomplete.errors.geolocationUnavailable')
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={isGeolocationAvailable ? onLocationClick : undefined}
          >
            <EmtIcon className="emt-direction" color="primary" />
            <EmtTypography variant="body1">{t('addressAutocomplete.geolocate')}</EmtTypography>
          </Box>
        )
      }
    />
  )
}

export default AddressAutocomplete
